import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError, fileUrl } from "@/lib/api";
import PortalHeader from "@/components/PortalHeader";
import StatusBadge from "@/components/StatusBadge";
import SmsTapToSend from "@/components/SmsTapToSend";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, Plus, Trash2, X, Calendar as CalendarIcon,
  Clock, User, Image as ImageIcon, FileText, Layers, ArrowLeft, Edit2,
  CheckCircle2, EyeOff, Eye, Sparkles
} from "lucide-react";

// Mobile-first calendar with three views: Day, Week, Month.

const VIEW_KEYS = ["day", "week", "month"];

export default function AdminCalendar() {
  const [view, setView] = useState("week");
  const [anchorDate, setAnchorDate] = useState(() => {
    const d = new Date(); d.setHours(0,0,0,0); return d;
  });
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [composer, setComposer] = useState(null); // {start, end} for new slot
  const [busy, setBusy] = useState(false);

  const range = useMemo(() => {
    const d = new Date(anchorDate);
    if (view === "day") {
      const start = new Date(d); start.setHours(0,0,0,0);
      const end = new Date(d); end.setHours(23,59,59,999);
      return { start, end };
    }
    if (view === "week") {
      const dow = d.getDay(); // 0=Sun
      const start = new Date(d); start.setDate(d.getDate() - dow); start.setHours(0,0,0,0);
      const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23,59,59,999);
      return { start, end };
    }
    // month
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }, [anchorDate, view]);

  const load = useCallback(async () => {
    try {
      const params = { start: range.start.toISOString(), end: range.end.toISOString() };
      const [a, s] = await Promise.all([
        http.get("/admin/calendar", { params }),
        http.get("/admin/slots", { params }),
      ]);
      setAppointments(a.data);
      setSlots(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const shift = (delta) => {
    const d = new Date(anchorDate);
    if (view === "day") d.setDate(d.getDate() + delta);
    else if (view === "week") d.setDate(d.getDate() + 7 * delta);
    else d.setMonth(d.getMonth() + delta);
    setAnchorDate(d);
  };

  const goToday = () => {
    const d = new Date(); d.setHours(0,0,0,0); setAnchorDate(d);
  };

  const seedTemplate = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/slots/seed-template?weeks=8");
      toast.success(`Added ${data.inserted} new slots`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const headerLabel = useMemo(() => {
    if (view === "day") return anchorDate.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
    if (view === "week") return `${range.start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${range.end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    return anchorDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [view, anchorDate, range]);

  return (
    <div className="min-h-screen bg-[#0A120E]">
      <PortalHeader title="Calendar" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
        {/* Top controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="flex items-center gap-2 text-[#A3A8A4] hover:text-[#D2A853]" data-testid="cal-back-admin">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.4} />
              <span className="overline">Console</span>
            </Link>
            <span className="hidden sm:block text-[#5A665D]">·</span>
            <span className="overline text-[#D2A853]">Calendar</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={seedTemplate} disabled={busy} className="flex items-center gap-2 text-[#D2A853] hover:text-[#E2BC6A] overline px-3 py-2 border border-[#D2A853]/30 hover:border-[#D2A853]/60 transition" data-testid="seed-template-btn">
              <Sparkles className="w-3 h-3" /> Seed 8wk Template
            </button>
            <button onClick={() => setComposer({})} className="btn-brass !px-4 !py-2 flex items-center gap-2" data-testid="cal-new-slot-btn">
              <Plus className="w-4 h-4" /> Slot
            </button>
          </div>
        </div>

        {/* View toggle + nav */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex border border-[#D2A853]/30">
            {VIEW_KEYS.map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`view-${v}`}
                className={`px-4 py-2 overline text-xs ${
                  view === v ? "bg-[#D2A853] text-[#0A120E]" : "text-[#A3A8A4] hover:text-[#E8E9E3]"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="p-2 border border-[#D2A853]/30 hover:border-[#D2A853] text-[#D2A853]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToday} className="overline text-[#A3A8A4] hover:text-[#D2A853] px-3 py-2 border border-[#D2A853]/30" data-testid="cal-today">Today</button>
            <button onClick={() => shift(1)} className="p-2 border border-[#D2A853]/30 hover:border-[#D2A853] text-[#D2A853]" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="font-display text-2xl sm:text-3xl text-[#E8E9E3] mb-6" data-testid="cal-header-label">{headerLabel}</div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#D2A853]" /></div>
        ) : (
          <>
            {view === "day" && <DayView date={anchorDate} appointments={appointments} slots={slots} onSelectAppt={setSelectedAppt} />}
            {view === "week" && <WeekView range={range} appointments={appointments} slots={slots} onSelectAppt={setSelectedAppt} />}
            {view === "month" && <MonthView anchor={anchorDate} appointments={appointments} slots={slots} onSelectAppt={setSelectedAppt} onPickDate={(d) => { setAnchorDate(d); setView("day"); }} />}
          </>
        )}
      </div>

      {selectedAppt && <AppointmentDrawer apptId={selectedAppt} onClose={() => setSelectedAppt(null)} onChanged={load} />}
      {composer && <SlotComposer initial={composer} onClose={() => setComposer(null)} onSaved={() => { setComposer(null); load(); }} />}
    </div>
  );
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function DayView({ date, appointments, slots, onSelectAppt }) {
  const dayAppts = appointments.filter((a) => sameDay(new Date(a.date), date));
  const daySlots = slots.filter((s) => sameDay(new Date(s.start), date));
  return (
    <div className="space-y-6">
      <Section title={`Appointments (${dayAppts.length})`}>
        {dayAppts.length === 0 && <Empty text="Nothing booked." />}
        <div className="space-y-2">
          {dayAppts.map((a) => <ApptRow key={a.id} appt={a} onClick={() => onSelectAppt(a.id)} />)}
        </div>
      </Section>
      <Section title={`Available Slots (${daySlots.filter((s) => !s.client_id).length})`}>
        {daySlots.length === 0 && <Empty text="No published slots." />}
        <div className="grid grid-cols-1 gap-2">
          {daySlots.map((s) => <SlotRow key={s.id} slot={s} />)}
        </div>
      </Section>
    </div>
  );
}

function WeekView({ range, appointments, slots, onSelectAppt }) {
  const days = [];
  const cur = new Date(range.start);
  while (cur <= range.end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-view">
      {days.map((d) => {
        const dayAppts = appointments.filter((a) => sameDay(new Date(a.date), d));
        const daySlots = slots.filter((s) => sameDay(new Date(s.start), d));
        const isToday = sameDay(d, new Date());
        return (
          <div key={d.toISOString()} className={`frame-card p-3 ${isToday ? "border-[#D2A853] bg-[#D2A853]/5" : ""}`}>
            <div className="flex items-baseline justify-between mb-2 pb-2 border-b border-[#D2A853]/15">
              <span className="overline text-[#D2A853] text-[10px]">{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
              <span className={`font-display text-xl ${isToday ? "text-[#D2A853]" : "text-[#E8E9E3]"}`}>{d.getDate()}</span>
            </div>
            <div className="space-y-1.5">
              {dayAppts.length === 0 && daySlots.filter((s) => !s.client_id).length === 0 && (
                <span className="text-[10px] text-[#5A665D]">—</span>
              )}
              {dayAppts.map((a) => (
                <button key={a.id} onClick={() => onSelectAppt(a.id)} className="block w-full text-left bg-[#D2A853]/15 border border-[#D2A853]/40 px-2 py-1.5 hover:bg-[#D2A853]/25 transition" data-testid={`appt-chip-${a.id}`}>
                  <div className="text-[11px] text-[#D2A853]">{fmtTime(a.date)}</div>
                  <div className="text-xs text-[#E8E9E3] truncate">{a.client_name || "—"}</div>
                </button>
              ))}
              {daySlots.filter((s) => !s.client_id).map((s) => (
                <div key={s.id} className="bg-[#1A241E] border border-[#D2A853]/15 px-2 py-1 opacity-70">
                  <div className="text-[10px] text-[#A3A8A4]">{fmtTime(s.start)}–{fmtTime(s.end)}</div>
                  <div className="text-[10px] text-[#5A665D] truncate">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, appointments, slots, onSelectAppt, onPickDate }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startWeekday = first.getDay();
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const totalDays = last.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div data-testid="month-view">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="overline text-[10px] text-[#A3A8A4] text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="aspect-square sm:aspect-auto sm:min-h-[80px]" />;
          const dayAppts = appointments.filter((a) => sameDay(new Date(a.date), c));
          const daySlots = slots.filter((s) => sameDay(new Date(s.start), c) && !s.client_id);
          const isToday = sameDay(c, new Date());
          return (
            <button
              key={c.toISOString()}
              onClick={() => onPickDate(c)}
              className={`text-left p-2 border ${isToday ? "border-[#D2A853] bg-[#D2A853]/5" : "border-[#D2A853]/15"} hover:border-[#D2A853]/60 min-h-[70px] sm:min-h-[90px] flex flex-col`}
              data-testid={`month-cell-${c.toISOString().slice(0,10)}`}
            >
              <span className={`font-display text-base ${isToday ? "text-[#D2A853]" : "text-[#E8E9E3]"}`}>{c.getDate()}</span>
              <div className="mt-auto space-y-1">
                {dayAppts.slice(0, 2).map((a) => (
                  <div key={a.id} className="text-[10px] bg-[#D2A853]/20 text-[#E8E9E3] px-1 py-0.5 truncate">
                    {fmtTime(a.date)} · {a.client_name || "—"}
                  </div>
                ))}
                {dayAppts.length > 2 && (
                  <div className="text-[10px] text-[#D2A853]">+{dayAppts.length - 2}</div>
                )}
                {dayAppts.length === 0 && daySlots.length > 0 && (
                  <div className="text-[10px] text-[#A3A8A4]">{daySlots.length} open</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApptRow({ appt, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 frame-card p-4 hover:border-[#D2A853] transition text-left"
      data-testid={`appt-row-${appt.id}`}
    >
      <div className="font-display text-xl text-[#D2A853] min-w-[90px]">{fmtTime(appt.date)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[#E8E9E3] truncate">{appt.client_name || "—"}</div>
        <div className="text-xs text-[#A3A8A4]">
          {appt.duration_minutes} min{appt.is_external ? " · external" : ""}
        </div>
      </div>
      {appt.client_status && <StatusBadge status={appt.client_status} />}
    </button>
  );
}

function SlotRow({ slot }) {
  return (
    <div className="flex items-center gap-3 frame-card p-3">
      <Clock className="w-4 h-4 text-[#D2A853]" strokeWidth={1.4} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#E8E9E3] truncate">{slot.label}</div>
        <div className="text-xs text-[#A3A8A4]">{fmtTime(slot.start)} – {fmtTime(slot.end)}</div>
      </div>
      <SlotActions slot={slot} />
    </div>
  );
}

function SlotActions({ slot }) {
  const [busy, setBusy] = useState(false);
  if (slot.client_id) return <span className="overline text-[10px] text-[#D2A853]">booked</span>;
  const togglePub = async () => {
    setBusy(true);
    try {
      await http.patch(`/admin/slots/${slot.id}`, { is_published: !slot.is_published });
      toast.success(slot.is_published ? "Hidden from clients" : "Published");
      window.dispatchEvent(new Event("slot-updated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this slot?")) return;
    setBusy(true);
    try {
      await http.delete(`/admin/slots/${slot.id}`);
      toast.success("Deleted");
      window.dispatchEvent(new Event("slot-updated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1">
      <button disabled={busy} onClick={togglePub} className="p-2 text-[#A3A8A4] hover:text-[#D2A853]" title={slot.is_published ? "Hide" : "Publish"} data-testid={`slot-toggle-${slot.id}`}>
        {slot.is_published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      </button>
      <button disabled={busy} onClick={del} className="p-2 text-[#A3A8A4] hover:text-[#CF6679]" data-testid={`slot-delete-${slot.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="overline text-[#D2A853] mb-3">{title}</div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="frame-card p-6 text-center text-sm text-[#A3A8A4]">{text}</div>;
}

function SlotComposer({ initial, onClose, onSaved }) {
  const [start, setStart] = useState(initial.start || "");
  const [end, setEnd] = useState(initial.end || "");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!start || !end || !label) { toast.error("All fields required"); return; }
    setBusy(true);
    try {
      const startIso = new Date(start).toISOString();
      const endIso = new Date(end).toISOString();
      await http.post("/admin/slots", { start: startIso, end: endIso, label });
      toast.success("Slot published");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-[#0A120E]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="frame-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="slot-composer">
        <div className="flex items-center justify-between mb-6">
          <span className="overline text-[#D2A853]">New Slot</span>
          <button onClick={onClose} className="text-[#A3A8A4] hover:text-[#D2A853]" data-testid="composer-close"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <Field label="Start"><input type="datetime-local" className="input-line" value={start} onChange={(e) => setStart(e.target.value)} data-testid="slot-start" /></Field>
          <Field label="End"><input type="datetime-local" className="input-line" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="slot-end" /></Field>
          <Field label="Label"><input className="input-line" placeholder="e.g. Half Day · 12pm – 4pm" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="slot-label" /></Field>
          <button type="submit" disabled={busy} className="btn-brass w-full flex items-center justify-center gap-2" data-testid="slot-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Publish Slot
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline text-[#A3A8A4] block mb-2">{label}</label>
      {children}
    </div>
  );
}

function AppointmentDrawer({ apptId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await http.get(`/admin/appointments/${apptId}`);
      setData(d);
      const a = d.appointment || {};
      setEditStart(a.date ? toLocalInput(a.date) : "");
      setEditEnd(a.end ? toLocalInput(a.end) : (a.date ? toLocalInput(a.date) : ""));
      setEditNotes(a.notes || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, [apptId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/admin/appointments/${apptId}`, {
        start: new Date(editStart).toISOString(),
        end: new Date(editEnd).toISOString(),
        notes: editNotes,
      });
      toast.success("Updated");
      setEditing(false);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this appointment?")) return;
    setBusy(true);
    try {
      await http.delete(`/admin/appointments/${apptId}`);
      toast.success("Deleted");
      onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (<div className="fixed inset-0 z-40 bg-[#0A120E]/80 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#D2A853]" /></div>);
  }
  const a = data.appointment || {};
  const c = data.client;
  return (
    <div className="fixed inset-0 z-40 bg-[#0A120E]/80 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="appt-drawer">
      <div className="w-full max-w-2xl h-full bg-[#0A120E] border-l border-[#D2A853]/30 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-[#0A120E]/80 border-b border-[#D2A853]/20 px-6 py-5 flex items-center justify-between">
          <div>
            <span className="overline text-[#D2A853]">Appointment</span>
            <h2 className="font-display text-2xl text-[#E8E9E3] mt-1">
              {new Date(a.date).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#A3A8A4] hover:text-[#D2A853]" data-testid="appt-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {c && (
            <div>
              <span className="overline text-[#D2A853]">Client</span>
              <div className="frame-card p-5 mt-3">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#D2A853]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#D2A853]" strokeWidth={1.4} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl text-[#E8E9E3]">{c.name}</div>
                    <div className="text-sm text-[#A3A8A4]">{c.email}</div>
                    <div className="text-sm text-[#A3A8A4]">{c.phone || "—"}</div>
                    <div className="mt-3"><StatusBadge status={c.status} /></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit appointment */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="overline text-[#D2A853]">When</span>
              <div className="flex gap-2">
                <button onClick={() => setEditing(!editing)} className="overline text-[#A3A8A4] hover:text-[#D2A853] flex items-center gap-1" data-testid="appt-edit-toggle">
                  <Edit2 className="w-3 h-3" /> {editing ? "Cancel" : "Edit"}
                </button>
                <button onClick={del} disabled={busy} className="overline text-[#CF6679] hover:text-[#CF6679] flex items-center gap-1" data-testid="appt-delete">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
            {editing ? (
              <div className="frame-card p-5 space-y-3">
                <Field label="Start"><input type="datetime-local" className="input-line" value={editStart} onChange={(e) => setEditStart(e.target.value)} data-testid="appt-edit-start" /></Field>
                <Field label="End"><input type="datetime-local" className="input-line" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} data-testid="appt-edit-end" /></Field>
                <Field label="Notes"><textarea rows={2} className="input-line resize-none" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} data-testid="appt-edit-notes" /></Field>
                <button onClick={save} disabled={busy} className="btn-brass w-full" data-testid="appt-save">Save Changes</button>
              </div>
            ) : (
              <div className="frame-card p-5">
                <div className="text-[#D2A853] font-display text-lg">{new Date(a.date).toLocaleString()}</div>
                <div className="text-sm text-[#A3A8A4]">{a.duration_minutes} minutes · {a.source || "manual"}</div>
                {a.notes && <p className="text-sm text-[#E8E9E3] mt-3">{a.notes}</p>}
              </div>
            )}
          </div>

          {data.payment && (
            <div>
              <span className="overline text-[#D2A853]">Deposit</span>
              <div className="frame-card p-5 mt-3 text-sm text-[#E8E9E3]">
                Status: <span className="text-[#D2A853]">{data.payment.status}</span>
                {data.payment.amount && <span className="text-[#A3A8A4]"> · ${data.payment.amount}</span>}
              </div>
            </div>
          )}

          {data.intake && (
            <div>
              <span className="overline text-[#D2A853]">Project</span>
              <div className="frame-card p-5 mt-3">
                <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap mb-3">{data.intake.project_description}</p>
                <div className="text-xs text-[#A3A8A4]">{data.intake.placement} · {data.intake.size}</div>
              </div>
            </div>
          )}

          {data.files?.length > 0 && (
            <div>
              <span className="overline text-[#D2A853]">References</span>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                {data.files.map((f) => (
                  <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" className="aspect-square border border-[#D2A853]/20 hover:border-[#D2A853]/60 overflow-hidden">
                    <img src={fileUrl(f.id)} alt={f.filename} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {data.notes?.length > 0 && (
            <div>
              <span className="overline text-[#D2A853]">Notes</span>
              <div className="space-y-2 mt-3">
                {data.notes.map((n) => (
                  <div key={n.id} className="frame-card p-4">
                    <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap">{n.text}</p>
                    <span className="text-[10px] text-[#A3A8A4] mt-2 block">{n.author_name} · {new Date(n.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <span className="overline text-[#D2A853]">Tap-to-Send Reminders</span>
            <p className="text-xs text-[#A3A8A4] mt-2 mb-3 leading-relaxed">
              Open your phone&rsquo;s SMS app pre-filled, send the message, then mark it as sent.
            </p>
            <SmsTapToSend appointmentId={apptId} onChanged={onChanged} />
          </div>
        </div>
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
